// src/components/SketchCanvas.jsx
import React, {
  forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState,
} from 'react';

// Paper and ink for a drawing that is going to be READ, not just looked at. Line
// extraction works on contrast, so the sheet is white and the pen near-black.
// The old default — cyan on a transparent sheet — gave it almost nothing.
const PAPER = '#ffffff';
const INK = '#111111';

/**
 * A freehand drawing surface for a mouse, a pen or a finger.
 *
 * Pointer events, not separate mouse and touch handlers: one path for all three,
 * including pens that report pressure. The canvas carries `touch-action: none`,
 * so a finger draws instead of scrolling the page — the old touch handlers never
 * prevented that, which made the sketch unusable on a tablet.
 *
 * The backing store is sized by devicePixelRatio so lines stay sharp on a dense
 * screen. The parent gets `exportPng()` and `hasInk` through a ref.
 */
const SketchCanvas = forwardRef(function SketchCanvas({ onInkChange }, ref) {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const [tool, setTool] = useState('pen');            // 'pen' | 'eraser'
  const [penSize, setPenSize] = useState(3);
  const [eraserSize, setEraserSize] = useState(18);
  const [colour, setColour] = useState(INK);
  const [popover, setPopover] = useState(null);       // 'pen' | 'eraser' | null
  const [hasInk, setHasInk] = useState(false);

  const markInk = useCallback((v) => {
    setHasInk(v);
    if (onInkChange) onInkChange(v);
  }, [onInkChange]);

  // Fit the backing store to the element and the screen density, keeping what is
  // already drawn. A resize redraws the old bitmap at its ORIGINAL pixel size
  // instead of stretching it into the new box.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const fit = () => {
      const dpr = window.devicePixelRatio || 1;
      const w = Math.max(1, Math.round(canvas.clientWidth * dpr));
      const h = Math.max(1, Math.round(canvas.clientHeight * dpr));
      if (canvas.width === w && canvas.height === h) return;
      const prev = document.createElement('canvas');
      prev.width = canvas.width;
      prev.height = canvas.height;
      if (prev.width && prev.height) prev.getContext('2d').drawImage(canvas, 0, 0);
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = PAPER;
      ctx.fillRect(0, 0, w, h);
      if (prev.width && prev.height) ctx.drawImage(prev, 0, 0);
    };
    fit();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
    if (ro) ro.observe(canvas); else window.addEventListener('resize', fit);
    return () => { if (ro) ro.disconnect(); else window.removeEventListener('resize', fit); };
  }, []);

  const toCanvas = (ev) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    return {
      x: (ev.clientX - r.left) * (c.width / (r.width || 1)),
      y: (ev.clientY - r.top) * (c.height / (r.height || 1)),
      k: c.width / (r.width || 1),
    };
  };

  const strokeTo = (p, pressure) => {
    const ctx = canvasRef.current.getContext('2d');
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    // The eraser paints paper rather than cutting a hole, so the export is always
    // ink on white — never ink on transparency that a viewer renders as black.
    ctx.strokeStyle = tool === 'eraser' ? PAPER : colour;
    const base = tool === 'eraser' ? eraserSize : penSize;
    // A pen that reports real pressure thins and thickens; mouse and touch report
    // a flat 0.5, which leaves the width alone.
    const pr = tool === 'pen' && pressure > 0 && pressure !== 0.5 ? 0.4 + pressure * 1.2 : 1;
    ctx.lineWidth = base * p.k * pr;
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
  };

  const onPointerDown = (e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    try { canvasRef.current.setPointerCapture(e.pointerId); } catch { /* older browsers */ }
    drawing.current = true;
    setPopover(null);
    const p = toCanvas(e);
    last.current = p;
    // A zero-length segment is pruned by the canvas, so nudge it: a tap leaves a dot.
    strokeTo({ ...p, x: p.x + 0.01 }, e.pressure);
    if (tool === 'pen' && !hasInk) markInk(true);
  };

  const onPointerMove = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const native = e.nativeEvent;
    const list = native.getCoalescedEvents ? native.getCoalescedEvents() : [];
    (list.length ? list : [native]).forEach((ev) => strokeTo(toCanvas(ev), ev.pressure));
  };

  const stop = (e) => {
    if (!drawing.current) return;
    drawing.current = false;
    last.current = null;
    try { canvasRef.current.releasePointerCapture(e.pointerId); } catch { /* not captured */ }
  };

  const clear = () => {
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    ctx.fillStyle = PAPER;
    ctx.fillRect(0, 0, c.width, c.height);
    markInk(false);
  };

  useImperativeHandle(ref, () => ({
    hasInk,
    /** The drawing as a PNG, white paper included. */
    exportPng: () => new Promise((resolve, reject) => {
      const c = canvasRef.current;
      if (!c) { reject(new Error('The drawing surface is not ready.')); return; }
      c.toBlob((b) => (b ? resolve(b) : reject(new Error('The drawing could not be exported.'))), 'image/png');
    }),
  }), [hasInk]);

  const sizePopover = (which, value, set, min, max) => (
    <div className="sketch-size-popover">
      <label className="sketch-size-label">
        {which === 'pen' ? 'Pen' : 'Eraser'} size · {value}
        <input
          type="range" min={min} max={max} value={value}
          onChange={(e) => set(Number(e.target.value))}
          className="sketch-size-input"
        />
      </label>
    </div>
  );

  return (
    <div className="sketch-wrapper">
      <div className="sketch-toolbar" role="toolbar" aria-label="Drawing tools">
        <div className="sketch-tool-group">
          <button type="button" className={`sketch-btn${tool === 'pen' ? ' active' : ''}`}
                  aria-pressed={tool === 'pen'} onClick={() => setTool('pen')}>Pen</button>
          <button type="button" className="sketch-arrow-btn" aria-label="Pen size"
                  aria-expanded={popover === 'pen'}
                  onClick={() => setPopover((v) => (v === 'pen' ? null : 'pen'))}>▾</button>
          {popover === 'pen' && sizePopover('pen', penSize, setPenSize, 1, 20)}
        </div>

        <div className="sketch-tool-group">
          <button type="button" className={`sketch-btn${tool === 'eraser' ? ' active' : ''}`}
                  aria-pressed={tool === 'eraser'} onClick={() => setTool('eraser')}>Eraser</button>
          <button type="button" className="sketch-arrow-btn" aria-label="Eraser size"
                  aria-expanded={popover === 'eraser'}
                  onClick={() => setPopover((v) => (v === 'eraser' ? null : 'eraser'))}>▾</button>
          {popover === 'eraser' && sizePopover('eraser', eraserSize, setEraserSize, 6, 48)}
        </div>

        <label className="sketch-color-label">
          Colour
          <input type="color" value={colour} onChange={(e) => setColour(e.target.value)}
                 className="sketch-color-input" />
        </label>
        <button type="button" className="sketch-btn" onClick={clear} disabled={!hasInk}>Clear</button>
      </div>

      <canvas
        ref={canvasRef}
        className="sketch-canvas"
        role="img"
        aria-label="Drawing area — draw with a mouse, a pen or a finger"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={stop}
        onPointerCancel={stop}
        onContextMenu={(e) => e.preventDefault()}
      />
      {!hasInk && <div className="sketch-hint" aria-hidden="true">Draw here — mouse, pen or finger</div>}
    </div>
  );
});

export default SketchCanvas;
